import type { Language, Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Heading,
  IconEducation,
  IconGlobe,
  IconLocation,
  Inline,
  Stack,
  Strong,
  Text,
  TooltipRenderer,
  useResponsiveValue,
} from 'braid-design-system';
import type { FC, ReactElement, ReactNode } from 'react';

import { ContactInfo } from 'src/App/AppPageBlock/ProfileDrawer/Profile/ContactInfo/ContactInfo';
import { StarRatingInput } from 'src/App/AppPageBlock/ProfileDrawer/Profile/StarRatingInput';
import { CertsyIdentityVerified } from 'src/App/CandidateManagementPage/JobApplicationCard/CertsyIdentityVerified';
import { convertLocationsIntoCommaList } from 'src/components/app/JobSection/utils';
import { Display } from 'src/libs/Display';
import type { BUCKETS } from 'src/stores/JobApplicationStore/types';

import { ApplicationBadgesInOrder } from '../../../ApplicationBadgesInOrder';
import { AppliedDate } from '../AppliedDate';

import translations from './.vocab';

interface ProfileHeaderProps {
  candidateId: string;
  jobApplicationId: string;
  fullName: string;
  certsyVerifiedFullName?: string;
  role: {
    mostRecentJobTitle?: string;
    mostRecentCompanyName?: string;
  };
  email: string;
  phone: string;
  shouldShowVerifiedCredentials: boolean;
  shouldShowRelevantApplicationBadge: boolean;
  shouldShowNewBadge: boolean;
  currentBucket: BUCKETS;
  homeLocation?: string;
  nationalities?: string[];
  education?: {
    name: string;
    institute: string;
    completionYear?: number;
  };
  appliedDate: string;
  source: number;
  rating: number;
  onClickRating: (index: number) => number;
  locale: Locale;
  language: Language;
  changeStatusButtons: ReactElement;
}

export const ProfileHeader: FC<ProfileHeaderProps> = ({
  candidateId,
  jobApplicationId,
  fullName,
  certsyVerifiedFullName,
  role: { mostRecentJobTitle, mostRecentCompanyName },
  email,
  phone,
  shouldShowVerifiedCredentials,
  shouldShowRelevantApplicationBadge,
  shouldShowNewBadge,
  currentBucket,
  homeLocation,
  nationalities,
  education,
  appliedDate,
  source,
  rating,
  onClickRating,
  locale,
  language,
  changeStatusButtons,
}) => {
  const { t } = useTranslations(translations);
  const responsiveValue = useResponsiveValue();
  const isDesktopOrAbove = responsiveValue({
    mobile: false,
    desktop: true,
  });

  const shouldShowHomeLocation = homeLocation && homeLocation !== '';
  const shouldShowNationalities = nationalities && nationalities.length > 0;
  const shouldShowEducation = education && education.name !== '';
  const shouldShowKeyInfo =
    shouldShowHomeLocation || shouldShowNationalities || shouldShowEducation;

  const mostRecentRole = (): ReactNode => {
    if (!mostRecentJobTitle) {
      return <Strong>{t('New to workforce')}</Strong>;
    }

    if (!mostRecentCompanyName) {
      return <Strong>{mostRecentJobTitle}</Strong>;
    }

    return t('mostRecentJobTitle at mostRecentCompanyName', {
      Strong: (children) => <Strong>{children}</Strong>,
      mostRecentJobTitle,
      mostRecentCompanyName,
    });
  };

  return (
    <Box data-cy="profile-header-details">
      <Stack space="medium">
        <Columns space="none">
          <Column>
            <Stack space="medium">
              <Stack space="small">
                <Heading level="2">
                  {fullName}{' '}
                  {certsyVerifiedFullName ? (
                    <CertsyIdentityVerified
                      idSuffix={jobApplicationId}
                      certsyVerifiedFullName={certsyVerifiedFullName}
                      boxProps={{
                        display: 'inline',
                      }}
                      iconSvgProps={
                        responsiveValue({
                          mobile: {
                            width: '19px',
                            height: '19px',
                          },
                          tablet: {
                            width: '21px',
                            height: '21px',
                          },
                        }) ?? { width: '21px', height: '21px' }
                      }
                    />
                  ) : null}
                </Heading>

                <Text size="small">{mostRecentRole()}</Text>

                <ContactInfo
                  email={email}
                  phone={phone}
                  candidateId={candidateId}
                />
              </Stack>

              <ApplicationBadgesInOrder
                shouldShowNewBadge={shouldShowNewBadge}
                shouldShowRelevantApplicationBadge={
                  shouldShowRelevantApplicationBadge
                }
                shouldShowVerifiedCredentialsBadge={
                  shouldShowVerifiedCredentials
                }
                jobApplicationId={jobApplicationId}
                currentBucket={currentBucket}
                language={language}
              />

              {shouldShowKeyInfo ? (
                <Stack space="small">
                  {shouldShowHomeLocation ? (
                    <Text size="small" icon={<IconLocation />}>
                      {homeLocation}
                    </Text>
                  ) : null}
                  {shouldShowNationalities ? (
                    <Text size="small" icon={<IconGlobe />}>
                      {nationalities &&
                        convertLocationsIntoCommaList(nationalities, language)}
                    </Text>
                  ) : null}
                  {shouldShowEducation ? (
                    <TooltipRenderer
                      id={`${candidateId}-education-tooltip`}
                      tooltip={<Text>{education!.institute}</Text>}
                    >
                      {({ triggerProps }) => (
                        <Box maxWidth="content" {...triggerProps}>
                          <Text size="small" icon={<IconEducation />}>
                            {education!.completionYear
                              ? `${education?.name}, ${education?.completionYear}`
                              : education?.name}
                          </Text>
                        </Box>
                      )}
                    </TooltipRenderer>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Column>
          <Column width={isDesktopOrAbove ? '1/3' : '1/4'} hideBelow="tablet">
            <Box
              style={{
                height: '100%',
                backgroundImage: `url(https://seekcdn.com/cm-ui/images/${
                  isDesktopOrAbove ? 'SeekShapesDesktop' : 'SeekShapesTablet'
                }.svg)`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top right',
                backgroundSize: 'auto',
                maxHeight: '180px',
                maxWidth: '282px',
                marginLeft: 'auto',
              }}
            />
          </Column>
        </Columns>

        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
        >
          <Box flexGrow={1} marginLeft="none" paddingLeft="none">
            <Inline space="medium" alignY="center">
              <AppliedDate
                appliedDate={appliedDate}
                source={source}
                locale={locale}
              />

              <StarRatingInput rating={rating} onClick={onClickRating} />
            </Inline>
          </Box>
          <Display tablet desktop>
            {changeStatusButtons}
          </Display>
        </Box>
      </Stack>
    </Box>
  );
};
